"use client";

// Client-side QR renderer. Uses the `nayuki-qr-code-generator` npm package
// (TypeScript port of nayuki/QR-Code-generator) to produce the native
// module matrix and draws the watermark corner-patch on top using Canvas2D
// (PNG) or a run-length-encoded <path> (SVG). Mirror of the server-side
// ZxingQrImageRenderer.kt, which links against `io.nayuki:qrcodegen`
// (same algorithm, byte-identical module matrix on both sides).

import qrcodegen from "nayuki-qr-code-generator";
import type {
  AnonymousDesign,
  AnonymousEcc,
  AnonymousPayload,
  AnonymousFormat,
} from "@qrody/api-client/render";
import {
  FRAME_ROUNDED_OUTER_RADIUS_MODULES,
  FRAME_SIMPLE_WIDTH_MODULES,
  MARGIN_MODULES,
  PATCH_HEIGHT_MODULES,
  PATCH_OUTER_MARGIN_BOTTOM_MODULES,
  PATCH_OUTER_MARGIN_LEFT_MODULES,
  PATCH_OUTER_MARGIN_RIGHT_MODULES,
  PATCH_OUTER_MARGIN_TOP_MODULES,
  PATCH_TEXT_PADDING_LEFT_MODULES,
  PATCH_TEXT_PADDING_RIGHT_MODULES,
  PATCH_V_PADDING_BOTTOM_MODULES,
  PATCH_V_PADDING_TOP_MODULES,
  PATCH_WIDTH_MODULES,
} from "./constants";
import {
  WATERMARK_PATH_D,
  WATERMARK_PATH_VIEWBOX,
} from "./watermark-path.generated";

// Opacity of the patch background rectangle. The fill *colour* itself is
// always design.background (i.e. whatever the user chose for the QR).
// Drop below 1.0 during layout work so the underlying QR modules show
// through — at 1.0 the patch is fully opaque and indistinguishable
// from the surrounding background (production default).
const PATCH_BG_OPACITY = 1.0;

/** Encode the payload into the flat string that the `qrcode` lib accepts. */
function payloadToString(p: AnonymousPayload): string {
  switch (p.type) {
    case "url":
      return p.url;
    case "text":
      return p.text;
    case "email": {
      const qs: string[] = [];
      if (p.subject) qs.push(`subject=${encodeURIComponent(p.subject)}`);
      if (p.body) qs.push(`body=${encodeURIComponent(p.body)}`);
      return `mailto:${p.to}${qs.length ? "?" + qs.join("&") : ""}`;
    }
    case "sms":
      return `SMSTO:${p.to}${p.message ? ":" + p.message : ""}`;
    case "geo":
      return `geo:${p.latitude},${p.longitude}`;
    case "wifi": {
      const esc = (s: string) => s.replace(/([\\;,:"])/g, "\\$1");
      const auth = p.authentication;
      const parts = [
        `T:${auth}`,
        `S:${esc(p.ssid)}`,
      ];
      if (auth !== "NOPASS" && p.password) parts.push(`P:${esc(p.password)}`);
      if (p.hidden) parts.push(`H:true`);
      return `WIFI:${parts.join(";")};;`;
    }
    case "vcard": {
      const lines = ["BEGIN:VCARD", "VERSION:3.0", `FN:${p.fullName}`];
      if (p.email) lines.push(`EMAIL:${p.email}`);
      if (p.phone) lines.push(`TEL:${p.phone}`);
      if (p.org) lines.push(`ORG:${p.org}`);
      if (p.title) lines.push(`TITLE:${p.title}`);
      if (p.url) lines.push(`URL:${p.url}`);
      lines.push("END:VCARD");
      return lines.join("\n");
    }
    case "file":
      return p.downloadUrl;
  }
}

interface ModuleMatrix {
  size: number; // side length in modules (without quiet zone)
  data: Uint8Array; // row-major, size*size entries
}

function toNayukiEcc(ecc: AnonymousEcc | undefined): qrcodegen.QrCode.Ecc {
  switch (ecc) {
    case "LOW": return qrcodegen.QrCode.Ecc.LOW;
    case "QUARTILE": return qrcodegen.QrCode.Ecc.QUARTILE;
    case "HIGH": return qrcodegen.QrCode.Ecc.HIGH;
    case "MEDIUM":
    default:
      return qrcodegen.QrCode.Ecc.MEDIUM;
  }
}

function buildMatrix(
  payload: AnonymousPayload,
  ecc: AnonymousEcc | undefined,
  minVersion?: number,
): ModuleMatrix {
  const qr = encodeQr(payload, ecc, minVersion);
  const size = qr.size;
  const data = new Uint8Array(size * size);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      data[y * size + x] = qr.getModule(x, y) ? 1 : 0;
    }
  }
  return { size, data };
}

function encodeQr(
  payload: AnonymousPayload,
  ecc: AnonymousEcc | undefined,
  minVersion?: number,
): qrcodegen.QrCode {
  const text = payloadToString(payload);
  const eccLevel = toNayukiEcc(ecc);
  if (!minVersion || minVersion < 1) {
    return qrcodegen.QrCode.encodeText(text, eccLevel);
  }
  // encodeSegments lets us set a min-version floor; the encoder still
  // bumps to a higher version automatically if the payload doesn't fit.
  // boostEcl=true means: if a higher ECC fits in the same version, use it.
  const segments = qrcodegen.QrSegment.makeSegments(text);
  const clamped = Math.min(40, Math.max(1, Math.round(minVersion)));
  return qrcodegen.QrCode.encodeSegments(segments, eccLevel, clamped, 40, -1, true);
}

/**
 * Builds the matrix and exposes the encoder's chosen version + mask.
 * Used by the editor caption so power users can see exactly which
 * QR-version + mask-pattern they got — mask is the encoder's penalty-
 * score winner across the eight candidates, so it changes when payload
 * or ECC changes even if the version stays the same.
 */
export function buildMatrixWithMeta(
  payload: AnonymousPayload,
  ecc: AnonymousEcc | undefined,
  minVersion?: number,
): { size: number; version: number; mask: number } {
  const qr = encodeQr(payload, ecc, minVersion);
  return { size: qr.size, version: qr.version, mask: qr.mask };
}

/**
 * QR-Spec-konforme Quiet-Zone-Breite: 4 Module pro Seite. Bei Berechnung
 * der Scannability-Mindestgröße wird die Quiet-Zone mitberücksichtigt,
 * weil sie Bestandteil der gerenderten Datei ist und mitgedruckt wird.
 */
const QUIET_ZONE_MODULES = 4;

/**
 * Returns the rendered side length in modules including the standard
 * 4-module quiet zone on each side. Used by the generator to compute
 * the px-resolution floor that keeps every module renderable at a
 * scannable px-per-module ratio.
 */
export function computeMatrixModules(
  payload: AnonymousPayload,
  ecc: AnonymousEcc | undefined,
  minVersion?: number,
): number {
  return buildMatrix(payload, ecc, minVersion).size + QUIET_ZONE_MODULES * 2;
}

/**
 * Probes whether the QR encoder can build a matrix for the given
 * payload at the requested ECC level. Higher ECC reserves more bits for
 * recovery data, so once the payload approaches the V40 byte-mode cap
 * MEDIUM/QUARTILE/HIGH may fail before LOW does. Used by the generator
 * to disable unreachable ECC options in the dropdown.
 */
export function isEccUsable(payload: AnonymousPayload, ecc: AnonymousEcc): boolean {
  try {
    buildMatrix(payload, ecc);
    return true;
  } catch {
    return false;
  }
}

/**
 * QR-Spec-konforme Versionsbreite (1–40). Konstanten exportiert,
 * damit der Generator den Mindestversion-Slider bounded clamp.
 */
export const QR_MIN_VERSION = 1;
export const QR_MAX_VERSION = 40;

/** Compute a uniform scale + translate that maps the WATERMARK_PATH_VIEWBOX
 *  bounding box into the target rectangle while preserving aspect ratio.
 *  Tune the patch dimensions via PATCH_WIDTH_MODULES / PATCH_HEIGHT_MODULES
 *  when the text length changes so the glyph height stays consistent. */
function fitPathTransform(targetX: number, targetY: number, targetW: number, targetH: number) {
  const scale = Math.min(
    targetW / WATERMARK_PATH_VIEWBOX.width,
    targetH / WATERMARK_PATH_VIEWBOX.height,
  );
  const scaledW = WATERMARK_PATH_VIEWBOX.width * scale;
  const scaledH = WATERMARK_PATH_VIEWBOX.height * scale;
  const tx = targetX + (targetW - scaledW) / 2 - WATERMARK_PATH_VIEWBOX.x * scale;
  const ty = targetY + (targetH - scaledH) / 2 - WATERMARK_PATH_VIEWBOX.y * scale;
  return { scale, tx, ty };
}

// ---------------------------------------------------------------------------
// PNG
// ---------------------------------------------------------------------------

function frameOutsetModules(design: AnonymousDesign): number {
  return design.frame === "SIMPLE" || design.frame === "ROUNDED"
    ? FRAME_SIMPLE_WIDTH_MODULES
    : 0;
}

function frameCornerRadiusModules(design: AnonymousDesign): number {
  return design.frame === "ROUNDED" ? FRAME_ROUNDED_OUTER_RADIUS_MODULES : 0;
}

function paintFrame(
  ctx: CanvasRenderingContext2D,
  canvasPx: number,
  outsetPx: number,
  cornerRadiusPx: number,
  design: AnonymousDesign,
) {
  if (outsetPx <= 0) return;
  ctx.fillStyle = design.foreground;
  if (cornerRadiusPx > 0) {
    ctx.beginPath();
    ctx.roundRect(0, 0, canvasPx, canvasPx, cornerRadiusPx);
    ctx.fill();
    const innerRadiusPx = Math.max(0, cornerRadiusPx - outsetPx);
    const innerSide = canvasPx - 2 * outsetPx;
    ctx.fillStyle = design.background;
    ctx.beginPath();
    ctx.roundRect(outsetPx, outsetPx, innerSide, innerSide, innerRadiusPx);
    ctx.fill();
  } else {
    ctx.fillRect(0, 0, canvasPx, outsetPx);
    ctx.fillRect(0, canvasPx - outsetPx, canvasPx, outsetPx);
    ctx.fillRect(0, outsetPx, outsetPx, canvasPx - 2 * outsetPx);
    ctx.fillRect(canvasPx - outsetPx, outsetPx, outsetPx, canvasPx - 2 * outsetPx);
  }
}

export function renderPngBlob(
  payload: AnonymousPayload,
  design: AnonymousDesign,
  sizePx: number,
  withBranding: boolean,
  minVersion?: number,
): Blob {
  const matrix = buildMatrix(payload, design.ecc, minVersion);
  const modules = matrix.size;
  const outset = frameOutsetModules(design);
  const cornerRadiusModules = frameCornerRadiusModules(design);
  const totalModules = modules + 2 * MARGIN_MODULES + 2 * outset;
  const moduleSizePx = Math.max(1, Math.floor(sizePx / totalModules));
  const actualPx = moduleSizePx * totalModules;
  const frameOutsetPx = outset * moduleSizePx;
  const cornerRadiusPx = cornerRadiusModules * moduleSizePx;

  const canvas = document.createElement("canvas");
  canvas.width = actualPx;
  canvas.height = actualPx;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context unavailable");

  // Background fill: full canvas only when there is no rounding. With a
  // ROUNDED frame the area outside the corner curve must stay transparent
  // — paintFrame paints the outer fg ring plus the inner bg fill, which
  // together cover only the rounded silhouette.
  if (cornerRadiusPx <= 0) {
    ctx.fillStyle = design.background;
    ctx.fillRect(0, 0, actualPx, actualPx);
  }

  // Frame below modules — the inner bg-filled rect (ROUNDED) carves the
  // ring hole without painting over data modules.
  paintFrame(ctx, actualPx, frameOutsetPx, cornerRadiusPx, design);

  const gridOffset = (MARGIN_MODULES + outset) * moduleSizePx;
  ctx.fillStyle = design.foreground;
  for (let y = 0; y < modules; y++) {
    for (let x = 0; x < modules; x++) {
      if (matrix.data[y * modules + x]) {
        ctx.fillRect(
          gridOffset + x * moduleSizePx,
          gridOffset + y * moduleSizePx,
          moduleSizePx,
          moduleSizePx,
        );
      }
    }
  }

  if (withBranding) {
    drawWatermarkPngPatch(ctx, actualPx, moduleSizePx, design, frameOutsetPx);
  }

  const dataUrl = canvas.toDataURL("image/png");
  return dataUrlToBlob(dataUrl);
}

function drawWatermarkPngPatch(
  ctx: CanvasRenderingContext2D,
  imgSize: number,
  moduleSizePx: number,
  design: AnonymousDesign,
  frameOutsetPx: number,
) {
  const patchWpx = PATCH_WIDTH_MODULES * moduleSizePx;
  const patchHpx = PATCH_HEIGHT_MODULES * moduleSizePx;
  // Outer margins per side — RIGHT + BOTTOM anchor the patch to the image
  // corner; TOP + LEFT extend the visual background further into the code.
  // With a frame we anchor to the inner edge (imgSize - frameOutsetPx)
  // so the patch stays in the same spot relative to the code.
  const marginRightPx = PATCH_OUTER_MARGIN_RIGHT_MODULES * moduleSizePx;
  const marginBottomPx = PATCH_OUTER_MARGIN_BOTTOM_MODULES * moduleSizePx;
  const marginLeftPx = PATCH_OUTER_MARGIN_LEFT_MODULES * moduleSizePx;
  const marginTopPx = PATCH_OUTER_MARGIN_TOP_MODULES * moduleSizePx;
  const innerEdge = imgSize - frameOutsetPx;
  const pX = innerEdge - marginRightPx - patchWpx;
  const pY = innerEdge - marginBottomPx - patchHpx;
  const bgX = pX - marginLeftPx;
  const bgY = pY - marginTopPx;
  const bgW = patchWpx + marginLeftPx;
  const bgH = patchHpx + marginTopPx;

  // Asymmetric inner padding: tight on top+left, roomy on bottom+right.
  const padLeftPx = PATCH_TEXT_PADDING_LEFT_MODULES * moduleSizePx;
  const padRightPx = PATCH_TEXT_PADDING_RIGHT_MODULES * moduleSizePx;
  const padTopPx = PATCH_V_PADDING_TOP_MODULES * moduleSizePx;
  const padBottomPx = PATCH_V_PADDING_BOTTOM_MODULES * moduleSizePx;
  const textAreaWidth = patchWpx - padLeftPx - padRightPx;
  const textAreaHeight = patchHpx - padTopPx - padBottomPx;

  // Patch background: always the design's background colour, optionally
  // transparent via PATCH_BG_OPACITY. Using globalAlpha + save/restore
  // so we don't hand-roll rgba() string manipulation.
  ctx.save();
  ctx.globalAlpha = PATCH_BG_OPACITY;
  ctx.fillStyle = design.background;
  ctx.fillRect(bgX, bgY, bgW, bgH);
  ctx.restore();

  // Watermark rendered from a pre-built Inkscape path (via scripts/
  // build-watermark-asset.sh). Deterministic across browsers — no font
  // metrics involved at runtime.
  const textAreaX = pX + padLeftPx;
  const textAreaY = pY + padTopPx;
  const { scale, tx, ty } = fitPathTransform(textAreaX, textAreaY, textAreaWidth, textAreaHeight);
  const path = new Path2D(WATERMARK_PATH_D);
  ctx.save();
  ctx.translate(tx, ty);
  ctx.scale(scale, scale);
  ctx.fillStyle = design.foreground;
  ctx.fill(path);
  ctx.restore();
}

function dataUrlToBlob(dataUrl: string): Blob {
  const commaIdx = dataUrl.indexOf(",");
  const header = commaIdx >= 0 ? dataUrl.slice(0, commaIdx) : "";
  const b64 = commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : "";
  const mime = /data:([^;]+)/.exec(header)?.[1] ?? "image/png";
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

// ---------------------------------------------------------------------------
// SVG
// ---------------------------------------------------------------------------

export function renderSvgBlob(
  payload: AnonymousPayload,
  design: AnonymousDesign,
  sizePx: number,
  withBranding: boolean,
  minVersion?: number,
): Blob {
  const matrix = buildMatrix(payload, design.ecc, minVersion);
  const modules = matrix.size;
  const outset = frameOutsetModules(design);
  const cornerRadius = frameCornerRadiusModules(design);
  const canvasSize = modules + 2 * MARGIN_MODULES + 2 * outset;
  const gridOffset = MARGIN_MODULES + outset;
  const innerEdge = canvasSize - outset;

  let svg = '<?xml version="1.0" encoding="UTF-8" standalone="no"?>\n';
  svg += '<!DOCTYPE svg PUBLIC "-//W3C//DTD SVG 1.1//EN" ';
  svg += '"http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd">\n';
  svg += `<svg xmlns="http://www.w3.org/2000/svg" version="1.1" `;
  svg += `width="${sizePx}" height="${sizePx}" `;
  svg += `viewBox="0 0 ${canvasSize} ${canvasSize}" shape-rendering="crispEdges">`;
  // Full-canvas bg fill only when there is no rounding. Under a ROUNDED
  // frame the area outside the corner curve must stay transparent —
  // buildFrameSvg paints the bg only inside the rounded silhouette.
  if (cornerRadius <= 0) {
    svg += `<rect width="100%" height="100%" fill="${design.background}"/>`;
  }

  svg += buildFrameSvg(canvasSize, outset, cornerRadius, design);

  svg += `<path fill="${design.foreground}" d="`;
  for (let y = 0; y < modules; y++) {
    let x = 0;
    while (x < modules) {
      if (matrix.data[y * modules + x]) {
        const start = x;
        while (x < modules && matrix.data[y * modules + x]) x++;
        const run = x - start;
        svg += `M${start + gridOffset} ${y + gridOffset}h${run}v1h-${run}z`;
      } else {
        x++;
      }
    }
  }
  svg += '"/>';

  if (withBranding) {
    const pX = innerEdge - PATCH_OUTER_MARGIN_RIGHT_MODULES - PATCH_WIDTH_MODULES;
    const pY = innerEdge - PATCH_OUTER_MARGIN_BOTTOM_MODULES - PATCH_HEIGHT_MODULES;
    const bgX = pX - PATCH_OUTER_MARGIN_LEFT_MODULES;
    const bgY = pY - PATCH_OUTER_MARGIN_TOP_MODULES;
    const bgW = PATCH_WIDTH_MODULES + PATCH_OUTER_MARGIN_LEFT_MODULES;
    const bgH = PATCH_HEIGHT_MODULES + PATCH_OUTER_MARGIN_TOP_MODULES;
    // Patch background: design's own bg colour + PATCH_BG_OPACITY alpha.
    svg += `<rect x="${bgX}" y="${bgY}" width="${bgW}" height="${bgH}" `;
    svg += `fill="${design.background}" fill-opacity="${PATCH_BG_OPACITY}"/>`;

    // Watermark from the pre-built path — same asset that Path2D uses in
    // the Canvas (PNG) path, so SVG and PNG are byte-identical modulo
    // the SVG renderer's own subpixel-AA rules.
    const textAreaWidth = PATCH_WIDTH_MODULES - PATCH_TEXT_PADDING_LEFT_MODULES - PATCH_TEXT_PADDING_RIGHT_MODULES;
    const textAreaHeight = PATCH_HEIGHT_MODULES - PATCH_V_PADDING_TOP_MODULES - PATCH_V_PADDING_BOTTOM_MODULES;
    const textAreaX = pX + PATCH_TEXT_PADDING_LEFT_MODULES;
    const textAreaY = pY + PATCH_V_PADDING_TOP_MODULES;
    const { scale, tx, ty } = fitPathTransform(textAreaX, textAreaY, textAreaWidth, textAreaHeight);
    // shape-rendering="geometricPrecision" overrides the outer SVG's
    // crispEdges for the watermark only — QR modules stay pixel-sharp,
    // glyph curves get proper antialiasing.
    svg += `<g shape-rendering="geometricPrecision" transform="translate(${tx} ${ty}) scale(${scale})">`;
    svg += `<path d="${WATERMARK_PATH_D}" fill="${design.foreground}"/>`;
    svg += `</g>`;
  }

  svg += "</svg>";
  return new Blob([svg], { type: "image/svg+xml" });
}

function buildFrameSvg(
  canvasSize: number,
  outset: number,
  cornerRadius: number,
  design: AnonymousDesign,
): string {
  if (outset <= 0) return "";
  if (cornerRadius > 0) {
    const innerRadius = Math.max(0, cornerRadius - outset);
    const innerSide = canvasSize - 2 * outset;
    let s = "";
    // shape-rendering override on the rounded rects only — the outer SVG
    // is crispEdges so QR modules stay pixel-sharp; without this override
    // the rounded corner gets rasterised into the integer-module grid and
    // looks visibly stair-stepped at typical viewport sizes.
    s += `<rect x="0" y="0" width="${canvasSize}" height="${canvasSize}"`;
    s += ` rx="${cornerRadius}" ry="${cornerRadius}" fill="${design.foreground}"`;
    s += ` shape-rendering="geometricPrecision"/>`;
    s += `<rect x="${outset}" y="${outset}" width="${innerSide}" height="${innerSide}"`;
    if (innerRadius > 0) s += ` rx="${innerRadius}" ry="${innerRadius}"`;
    s += ` fill="${design.background}" shape-rendering="geometricPrecision"/>`;
    return s;
  }
  let s = `<path fill="${design.foreground}" d="`;
  s += `M0 0h${canvasSize}v${outset}H0z`;
  s += `M0 ${canvasSize - outset}h${canvasSize}v${outset}H0z`;
  s += `M0 ${outset}h${outset}v${canvasSize - 2 * outset}H0z`;
  s += `M${canvasSize - outset} ${outset}h${outset}v${canvasSize - 2 * outset}h-${outset}z`;
  s += `"/>`;
  return s;
}

// ---------------------------------------------------------------------------
// Public API — mirrors the server-side renderAnonymous() shape.
// ---------------------------------------------------------------------------

export type ClientRenderInput = {
  payload: AnonymousPayload;
  design: AnonymousDesign;
  format: AnonymousFormat;
  withBranding: boolean;
  size: number;
  /** Optional QR-version floor (1–40). undefined = encoder picks the smallest. */
  minVersion?: number;
};

export type ClientRenderResult =
  | { ok: true; bytes: Blob; mediaType: string; rendered: "client" }
  | { ok: false; reason: "unsupported-format" | "empty-payload" | "encode-failed" };

/** Render PNG or SVG on the client. PDF is not supported — caller should
 *  fall back to the server endpoint for that. Empty payloads (e.g. text
 *  field cleared) are surfaced as an `empty-payload` result instead of
 *  letting the underlying QR encoder throw on a zero-length input.
 *  Encoder failures (payload too large for the chosen ECC, exotic
 *  unicode that nayuki rejects, …) are also surfaced as a typed result
 *  rather than as a thrown exception, so the caller does not need a
 *  catch-all wrapper. */
export function renderClient(input: ClientRenderInput): ClientRenderResult {
  if (input.format === "pdf") return { ok: false, reason: "unsupported-format" };
  if (!payloadToString(input.payload)) return { ok: false, reason: "empty-payload" };
  try {
    const blob =
      input.format === "png"
        ? renderPngBlob(input.payload, input.design, input.size, input.withBranding, input.minVersion)
        : renderSvgBlob(input.payload, input.design, input.size, input.withBranding, input.minVersion);
    return { ok: true, bytes: blob, mediaType: blob.type, rendered: "client" };
  } catch {
    return { ok: false, reason: "encode-failed" };
  }
}
